/**
 * Деньги товара — типы и чистые функции без базы: их читает и сервер
 * (lib/finance-product-money.ts считает и хранит), и браузер (ячейки
 * колонок money_* и «за период», строка модели складывает размеры). Всё,
 * что ходит в базу, — в lib/finance-product-money.ts.
 */
import { roundMoney } from "@/lib/store-json";

export const PRODUCT_MONEY_PERIODS = ["d7", "d14", "d30"] as const;
export type ProductMoneyPeriodKey = typeof PRODUCT_MONEY_PERIODS[number];
export const PERIOD_DAYS: Record<ProductMoneyPeriodKey, number> = { d7: 7, d14: 14, d30: 30 };

/** Деньги товара за период. Расходы — отрицательные, как в начислениях. */
export type ProductMoney = {
  /** Продажи по цене продавца (Россия) плюс продажи за рубеж по цене выкупа. */
  sales: number;
  returns: number;
  /** Комиссия минус возвращённая при возвратах. */
  commission: number;
  /** Услуги доставки, привязанные к товару. */
  delivery: number;
  /** Обратная логистика и обработка возвратов/невыкупов, привязанные к товару. */
  returnCosts: number;
  /** Расход на рекламу товара — из статистики кампаний. */
  ads: number;
  /** Прочие услуги и компенсации с SKU: склад, сборы, продвижение без рекламы. */
  otherDirect: number;
  cogs: number;
  /** Проданные штуки: выкупы − возвраты + за рубеж. */
  units: number;
  /** Штуки без себестоимости — прибыль по ним завышена. */
  unitsWithoutCost: number;
  marginProfit: number;
  /** Маржинальная прибыль ÷ (продажи − возвраты), %. */
  marginPct: number | null;
  /** Доля общих складских расходов — по проданным штукам. */
  generalWarehouse: number;
  /** Доля общих остальных расходов — по выручке. */
  generalOther: number;
  general: number;
  profit: number;
  profitPerUnit: number | null;
  /**
   * По видам услуг Ozon (тип начисления → сумма): у услуг с артикулом —
   * начислено на товар, у общих — доля товара по тому же правилу. Колонки
   * `money_type_<id>_<период>` — детали групп «Доставка», «Возвраты и
   * невыкупы», «Прочие услуги», «Общие расходы».
   */
  byType: Record<string, number>;

  // ── То же, что строки блока «Деньги магазина», по этому товару ──
  /** Проданные штуки до возвратов (Россия + за рубеж) — для среднего чека. */
  salesUnits: number;
  /** Продажи за рубеж (выкуп Ozon) по цене выкупа и штуки — уже входят в sales/units. */
  abroad: number;
  abroadUnits: number;
  /** НДС внутри цены выкупа за рубеж. */
  abroadVat: number;
  /** Оплачено покупателями своими деньгами (минус возвраты), баллы Ozon, доплаты Ozon и банка. */
  customerPaid: number;
  bonus: number;
  coinvest: number;
  /** Начислено Ozon по товару: продажи − возвраты − комиссия − услуги (свои и доля общих) + компенсации. */
  accrued: number;
  /** Прибыль до налогов ÷ себестоимость. */
  roi: number | null;
  /** Прибыль до налогов ÷ (выручка − себестоимость). */
  channelYield: number | null;
  /** Средняя комиссия: комиссия ÷ продажи по цене продавца. */
  commissionPct: number | null;
  /** Средний чек выкупа (цена продавца) и по цене покупателя. */
  avgCheck: number | null;
  avgCustomerCheck: number | null;
  /** Доли от выручки (продажи − возвраты), %. */
  /**
   * Куда уходит выручка, % (продажи − возвраты = 100): расходы положительными
   * долями, общие — со знаком (компенсации сверх расходов дают минус), налоги
   * — null, пока схема не задана; logistics — подытог доставка + обратная
   * логистика (в цепочку не входит). Цепочка сходится: 100 − (комиссия + доставка
   * + возвраты + реклама + прочие + себестоимость) = маржа %; маржа − общие =
   * прибыль до налогов %; прибыль − налоги = чистая %.
   */
  shares: { commission: number | null; delivery: number | null; returnCosts: number | null; logistics: number | null; ads: number | null; other: number | null; general: number | null; ozon: number | null; cogs: number | null; taxes: number | null };
  /** Прибыль до налогов в % от выручки. */
  profitPct: number | null;
  /** Налоги по правилам Ozon: доход с НДС и без, НДС, налог до и после взносов, взносы ИП, всего; null — схема не задана. */
  incomeGross: number;
  incomeNet: number | null;
  vat: number | null;
  mainTaxGross: number | null;
  contributions: number | null;
  mainTax: number | null;
  taxesTotal: number | null;
  /** Начислено минус НДС; минус все налоги (без себестоимости). */
  afterVat: number | null;
  afterTaxes: number | null;
  /** Ступени прибыли после НДС и после всех налогов — с маржой, ROI и на штуку. */
  afterVatProfit: number | null;
  afterVatMargin: number | null;
  afterVatRoi: number | null;
  afterVatPerUnit: number | null;
  netProfit: number | null;
  netMargin: number | null;
  netRoi: number | null;
  netPerUnit: number | null;
};

export type ProductMoneyPeriods = Record<ProductMoneyPeriodKey, ProductMoney>;
export type ProductMoneyByKey = Record<string, ProductMoneyPeriods>;

/** Куда попадает вид услуги в таблице: своя группа-итог, за которой раскрываются детали. */
export type MoneyTypeBucket = "delivery" | "returns" | "other" | "general";

/** Вид услуги Ozon, который есть у магазина, — для колонок по видам. */
export type MoneyTypeInfo = { id: number; label: string; bucket: MoneyTypeBucket };

export type ProductMoneyPayload = { items: ProductMoneyByKey; types: MoneyTypeInfo[] };

export function emptyProductMoney(): ProductMoney {
  return {
    sales: 0, returns: 0, commission: 0, delivery: 0, returnCosts: 0, ads: 0, otherDirect: 0, cogs: 0, units: 0, unitsWithoutCost: 0,
    marginProfit: 0, marginPct: null, generalWarehouse: 0, generalOther: 0, general: 0, profit: 0, profitPerUnit: null, byType: {},
    salesUnits: 0, abroad: 0, abroadUnits: 0, abroadVat: 0, customerPaid: 0, bonus: 0, coinvest: 0, accrued: 0,
    roi: null, channelYield: null, commissionPct: null, avgCheck: null, avgCustomerCheck: null,
    shares: { commission: null, delivery: null, returnCosts: null, logistics: null, ads: null, other: null, general: null, ozon: null, cogs: null, taxes: null },
    profitPct: null,
    incomeGross: 0, incomeNet: null, vat: null, mainTaxGross: null, contributions: null, mainTax: null, taxesTotal: null,
    afterVat: null, afterTaxes: null,
    afterVatProfit: null, afterVatMargin: null, afterVatRoi: null, afterVatPerUnit: null,
    netProfit: null, netMargin: null, netRoi: null, netPerUnit: null,
  };
}

export const ratio = (a: number | null, b: number | null, scale = 100): number | null => a == null || b == null || !(Math.abs(b) > 0) ? null : Math.round(a / b * scale * 10) / 10;
export const perUnit = (a: number | null, units: number): number | null => a == null || !(units > 0) ? null : roundMoney(a / units);

type NumericMoneyKey = {
  [K in keyof ProductMoney]: ProductMoney[K] extends number ? K : never;
}[keyof ProductMoney];
type NullableMoneyKey = {
  [K in keyof ProductMoney]: ProductMoney[K] extends number | null
    ? null extends ProductMoney[K] ? K : never : never;
}[keyof ProductMoney];

// Additive inputs only. Ratios/per-unit values are derived after the sum;
// nested byType/shares have their own aggregation stages below.
const SUM_MONEY_FIELDS = ["sales", "returns", "commission", "delivery", "returnCosts", "ads", "otherDirect", "cogs", "units", "unitsWithoutCost",
  "marginProfit", "generalWarehouse", "generalOther", "general", "profit", "salesUnits", "abroad", "abroadUnits", "abroadVat", "customerPaid", "bonus", "coinvest", "accrued", "incomeGross"] as const satisfies readonly NumericMoneyKey[];
const NULLABLE_MONEY_FIELDS = ["incomeNet", "vat", "mainTaxGross", "contributions", "mainTax", "taxesTotal", "afterVat", "afterTaxes", "afterVatProfit", "netProfit"] as const satisfies readonly NullableMoneyKey[];
const UNIT_MONEY_FIELDS = new Set<NumericMoneyKey>(["units", "unitsWithoutCost", "salesUnits", "abroadUnits"]);

/**
 * Деньги модели — сумма её размеров: аддитивные поля складываются, доли,
 * маржи и средние пересчитываются от сумм теми же формулами, что у одного
 * товара (finish). Нужна строке модели в таблице: у неё те же ячейки, цвет
 * убытка и подсказка «из чего сложилось», что у строк размеров. Налоги —
 * null, если хотя бы у одного размера они не посчитаны (схема не задана).
 */
export function aggregateProductMoney(items: readonly ProductMoney[]): ProductMoney {
  if (items.length === 1) return items[0];
  const out = emptyProductMoney();
  for (const key of NULLABLE_MONEY_FIELDS) out[key] = 0;
  const byType: Record<string, number> = {};
  for (const item of items) {
    for (const key of SUM_MONEY_FIELDS) out[key] += item[key];
    for (const key of NULLABLE_MONEY_FIELDS) {
      const value = item[key];
      out[key] = value == null || out[key] == null ? null : out[key]! + value;
    }
    for (const [typeId, amount] of Object.entries(item.byType)) byType[typeId] = (byType[typeId] ?? 0) + amount;
  }
  for (const key of SUM_MONEY_FIELDS) if (!UNIT_MONEY_FIELDS.has(key)) out[key] = roundMoney(out[key]);
  for (const key of NULLABLE_MONEY_FIELDS) out[key] = out[key] == null ? null : roundMoney(out[key]!);
  for (const [typeId, amount] of Object.entries(byType)) out.byType[typeId] = roundMoney(amount);
  const revenue = out.sales + out.returns;
  const rev = revenue > 0 ? revenue : null;
  const cogsAbs = Math.abs(out.cogs);
  const cogs = cogsAbs > 0 ? cogsAbs : null;
  out.marginPct = ratio(out.marginProfit, rev);
  out.profitPerUnit = perUnit(out.profit, out.units);
  out.roi = ratio(out.profit, cogs);
  out.channelYield = ratio(out.profit, revenue - cogsAbs > 0 ? revenue - cogsAbs : null);
  out.commissionPct = ratio(Math.abs(out.commission), out.sales - out.abroad > 0 ? out.sales - out.abroad : null);
  out.avgCheck = perUnit(out.sales, out.salesUnits);
  out.avgCustomerCheck = perUnit(out.customerPaid + out.abroad, out.units);
  out.shares = {
    commission: ratio(Math.abs(out.commission), rev), delivery: ratio(Math.abs(out.delivery), rev), returnCosts: ratio(Math.abs(out.returnCosts), rev),
    logistics: ratio(Math.abs(out.delivery) + Math.abs(out.returnCosts), rev),
    ads: ratio(Math.abs(out.ads), rev), other: ratio(Math.abs(out.otherDirect), rev), general: ratio(-out.general, rev),
    ozon: ratio(revenue - out.accrued, rev), cogs: ratio(cogsAbs, rev), taxes: ratio(out.taxesTotal, rev),
  };
  out.profitPct = ratio(out.profit, rev);
  out.afterVatMargin = ratio(out.afterVatProfit, rev); out.afterVatRoi = ratio(out.afterVatProfit, cogs); out.afterVatPerUnit = perUnit(out.afterVatProfit, out.units);
  out.netMargin = ratio(out.netProfit, rev); out.netRoi = ratio(out.netProfit, cogs); out.netPerUnit = perUnit(out.netProfit, out.units);
  return out;
}
